import { z } from "zod";
import { NotificationCategorySchema, type NotificationCategory } from "./notifications.js";
import type { PermissionSet } from "./permissions.js";

/**
 * Customer Slack notifications (#568), ported from crmarr/templates
 * `customer-slack-notifications.md` (proven in Mermera). An organization
 * connects its own Slack channels with Add to Slack; each environment's
 * notices are routed, category by category, to one of them or Off. One post
 * per event per channel, however many people receive it by email. Slack is
 * sent as well as email, never instead of it, and is never a person's own
 * choice.
 *
 * The routing scope is the environment (the template's cross-product
 * decision for Starsach: production alarms to `#ops`, dev to `#ops-dev`).
 */

/** The one named check for who connects and routes (the template's `canRouteSlack`), held per environment. */
export const SLACK_ROUTE_PERMISSION = "notifications:route";
export function canRouteSlack(permissions: Pick<PermissionSet, "has">): boolean {
  return permissions.has(SLACK_ROUTE_PERMISSION);
}

/** Every category can go to Slack; none is email-only yet. A category added later is routable and Off until routed. */
export const SLACK_ROUTABLE_CATEGORIES: readonly NotificationCategory[] = NotificationCategorySchema.options;
/** What a first channel gets on every environment that has never been routed: the two on-by-default notices. */
export const SLACK_DEFAULT_CATEGORIES: readonly NotificationCategory[] = ["stopped-reporting", "alarms-critical"];
/** The table stores lists, the page sends one; the API caps a list here (the template's rule). */
export const MAX_CHANNELS_PER_CATEGORY = 5;

/** A category's channels; a category with no entry is Off. */
export const SlackRoutesSchema = z.partialRecord(NotificationCategorySchema, z.array(z.string().min(1)).max(MAX_CHANNELS_PER_CATEGORY));
export type SlackRoutes = z.infer<typeof SlackRoutesSchema>;

export function defaultSlackRoutes(connectionId: string): SlackRoutes {
  return Object.fromEntries(SLACK_DEFAULT_CATEGORIES.map((c) => [c, [connectionId]]));
}

/** The distinct channels one category goes to. */
export function routedConnectionIds(routes: SlackRoutes | undefined, category: NotificationCategory): string[] {
  return [...new Set(routes?.[category] ?? [])];
}

/** A save that changes nothing is not audited. */
export function routesChanged(before: SlackRoutes, after: SlackRoutes): boolean {
  return SLACK_ROUTABLE_CATEGORIES.some((c) => JSON.stringify([...(before[c] ?? [])].sort()) !== JSON.stringify([...(after[c] ?? [])].sort()));
}

export const SlackConnectionStatusSchema = z.enum(["connected", "broken"]);
export type SlackConnectionStatus = z.infer<typeof SlackConnectionStatusSchema>;

/**
 * `SlackNotify`, SK `connection#<connection_id>`: one connected channel. The
 * webhook itself is never here - it is a bearer credential, kept in the
 * channel's own secret `starsach/<env>/slack-notify/<organisation>/<connection>`.
 */
export const SlackConnectionRecordSchema = z.object({
  organisation_id: z.string().min(1),
  sk: z.string().min(1),
  connection_id: z.string().min(1),
  team_id: z.string().min(1),
  team_name: z.string().optional(),
  channel_id: z.string().optional(),
  channel_name: z.string().optional(),
  status: SlackConnectionStatusSchema,
  broken_reason: z.string().optional(),
  connected_by: z.string(),
  connected_by_subject: z.string(),
  connected_at: z.string(),
  last_delivery_at: z.string().optional(),
  last_delivery_ok: z.boolean().optional(),
  last_delivery_error: z.string().optional(),
});
export type SlackConnectionRecord = z.infer<typeof SlackConnectionRecordSchema>;

/** `SlackNotify`, SK `routes#<environment_id>`: where one environment's notices go. No row: never routed, every category Off. */
export const SlackRoutesRecordSchema = z.object({
  organisation_id: z.string().min(1),
  sk: z.string().min(1),
  environment_id: z.string().min(1),
  routes: SlackRoutesSchema,
  updated_at: z.string(),
  updated_by: z.string(),
});
export type SlackRoutesRecord = z.infer<typeof SlackRoutesRecordSchema>;

/** One channel as the page shows it. */
export interface SlackConnectionView {
  connectionId: string;
  teamName: string | null;
  channel: string | null;
  status: SlackConnectionStatus;
  brokenReason: string | null;
  connectedBy: string;
  connectedAt: string;
  lastDeliveryAt: string | null;
  lastDeliveryOk: boolean | null;
  lastDeliveryError: string | null;
}

/** One environment's routing, for the environments the caller may see. */
export interface SlackEnvironmentRouting {
  environmentId: string;
  name: string;
  /** Whether the caller holds `notifications:route` here. */
  canRoute: boolean;
  /** Absent: never routed. */
  routes: SlackRoutes;
  routed: boolean;
}

/** `GET /v1/organisations/me/slack` */
export interface SlackNotifyStatusResponse {
  /** False until this environment's Slack app exists (its client id and secret are stored): the section says so and nothing fails. */
  available: boolean;
  /** Whether the caller may connect, test and disconnect: `notifications:route` on any environment. */
  canConnect: boolean;
  connections: SlackConnectionView[];
  environments: SlackEnvironmentRouting[];
}

export const SlackStartInputSchema = z.object({
  /** Where to come back to; it must be an environment the caller may route. */
  environmentId: z.string().min(1).max(128),
});
export type SlackStartInput = z.infer<typeof SlackStartInputSchema>;

/** `POST /v1/organisations/me/slack/start`: Slack's consent screen, with a signed state. */
export interface SlackStartResponse {
  url: string;
}

export const SlackConnectInputSchema = z.object({
  code: z.string().min(1).max(512).optional(),
  state: z.string().min(1).max(1024),
  /** Slack's `error` when the person pressed Cancel on its screen. */
  error: z.string().max(80).optional(),
});
export type SlackConnectInput = z.infer<typeof SlackConnectInputSchema>;

export type SlackConnectResult = "connected" | "canceled" | "expired" | "invalid" | "forbidden" | "failed" | "unavailable";

/** `POST /v1/organisations/me/slack/connect`: the page the redirect landed on finishes the connection, signed in. */
export interface SlackConnectResponse {
  result: SlackConnectResult;
  /** The environment the person started from, when the state could be read. */
  environmentId: string | null;
  connectionId: string | null;
  channel: string | null;
  reconnected: boolean;
}

export const SlackConnectionInputSchema = z.object({ connectionId: z.string().min(1).max(64) });
export type SlackConnectionInput = z.infer<typeof SlackConnectionInputSchema>;

/** `POST /v1/organisations/me/slack/test` */
export interface SlackTestResponse {
  sent: true;
}

/** `POST /v1/organisations/me/slack/disconnect` */
export interface SlackDisconnectResponse {
  disconnected: true;
  /** The environments whose routing named the channel; those categories read Off now. */
  environmentIds: string[];
}

export const SlackRoutesInputSchema = z.object({ routes: SlackRoutesSchema });
export type SlackRoutesInput = z.infer<typeof SlackRoutesInputSchema>;

/** `PUT /v1/environments/{environmentId}/slack-routes`: the routing as stored, read back. */
export interface SlackRoutesSavedResponse {
  environmentId: string;
  routes: SlackRoutes;
}

/**
 * The OAuth state's payload, readable by the page the redirect lands on (it
 * needs to know where to go back to) but trusted only by the API, which
 * checks its signature.
 */
export interface SlackStatePayload {
  v: 1;
  /** organisation */
  o: string;
  /** the person who clicked Add to Slack */
  s: string;
  /** the environment they started from */
  e: string;
  n: string;
  iat: number;
}
